import { generateURL, parseURL } from '@archipro-website/top-navigation';
import type { FiltersConfig } from '@rocco/ui/filter-sort/types/filter-config-section';
import type { FilterStates } from '@modules-rocco/filters/types/filter-states';
import type { SearchServiceFilters } from '@modules/search/type/api';
import type { FilterStatesKey } from '@modules/search/type/filter-state';
import { isUS } from '~/modules/root/page/us-footer-gating';

/** Professionals filter visibility restores for all US users after this instant (UTC). */
export const US_PROJECT_PROFESSIONALS_FILTER_RESTORE_ON = new Date(
    '2026-06-12T00:00:00Z',
);

interface ShouldHideUsProjectProfessionalsFilterArgs {
    countryCode: string;
    isArchiproStaff: boolean;
    now?: Date;
}

/** Whether the US project category Professionals filter should be hidden for the viewer. */
export const shouldHideUsProjectProfessionalsFilter = ({
    countryCode,
    isArchiproStaff,
    now = new Date(),
}: ShouldHideUsProjectProfessionalsFilterArgs): boolean =>
    isUS(countryCode) &&
    !isArchiproStaff &&
    now < US_PROJECT_PROFESSIONALS_FILTER_RESTORE_ON;

/** URL param key for the project Professionals filter on legacy directory pages. */
export const PROJECT_PROFESSIONALS_URL_PARAM = 'company';

export const getIsArchiproStaffFromMember = (user: {
    __typename?: string;
    IsArchiproStaff?: boolean;
}): boolean => user.__typename === 'Me' && user.IsArchiproStaff === true;

/** Normalises Remix `request.url` (absolute) to a path+query string for `parseURL`. */
const toPathAndQuery = (requestUrl: string): string => {
    if (requestUrl.startsWith('/')) {
        return requestUrl;
    }

    const { pathname, search } = new URL(requestUrl);
    return `${pathname}${search}`;
};

/** Returns a redirect target when `company` params must be stripped, otherwise null. */
export const getRedirectUrlWithoutProjectProfessionalsFilter = (
    requestUrl: string,
): string | null => {
    const { basePath, params } = parseURL(toPathAndQuery(requestUrl));
    const company = params[PROJECT_PROFESSIONALS_URL_PARAM];
    if (!company || (Array.isArray(company) && company.length === 0)) {
        return null;
    }

    const { [PROJECT_PROFESSIONALS_URL_PARAM]: _removed, ...rest } = params;
    return generateURL(basePath, rest, true);
};

export const stripProjectProfessionalsFromSearchFilters = (
    filters: SearchServiceFilters | undefined,
): void => {
    if (filters?.professionals) {
        delete filters.professionals;
    }
};

export const getProjectCategoryIgnoreFilterKeys = (
    hideProfessionalsFilter: boolean,
    baseKeys: FilterStatesKey[] = ['viewProjectsBy', 'awards'],
): FilterStatesKey[] =>
    hideProfessionalsFilter ? [...baseKeys, 'professionals'] : baseKeys;

/** Removes Professionals sections from rocco project filter config when gated. */
export const getProjectFiltersConfigForViewer = (
    config: FiltersConfig,
    hideProfessionalsFilter: boolean,
): FiltersConfig => {
    if (!hideProfessionalsFilter) {
        return config;
    }

    const withoutProfessionals = <T extends { title: string }>(sections: T[]) =>
        sections.filter((section) => section.title !== 'Professionals');

    return {
        ...config,
        barFilters: withoutProfessionals(config.barFilters),
        modalFilters: withoutProfessionals(config.modalFilters),
    };
};

/** Clears applied professionals filter state for rocco search requests. */
export const stripProfessionalsFromRoccoFilterStates = (
    filterStates: FilterStates,
): FilterStates => {
    const professionals = filterStates.states.professionals;
    if (!professionals?.selectedItems?.length) {
        return filterStates;
    }

    return {
        ...filterStates,
        states: {
            ...filterStates.states,
            professionals: {
                ...professionals,
                selectedItems: [],
            },
        },
    };
};
